import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine, Cell, PieChart, Pie } from 'recharts';
import { ShieldAlert, TrendingDown, Activity, AlertTriangle, Target, Scale } from 'lucide-react';
// Mock Data
const pnlHistory = Array.from({
  length: 24
}, (_, i) => ({
  time: `${i}:00`,
  pnl: Math.random() * 10000 - 2000 + i * 500,
  var: 5000 + Math.random() * 1000
}));
const riskMetrics = [{
  name: 'Delta',
  value: 450,
  limit: 1000,
  unit: 'MW'
}, {
  name: 'Gamma',
  value: 120,
  limit: 200,
  unit: 'MW/€'
}, {
  name: 'Vega',
  value: 85,
  limit: 100,
  unit: 'k€/%'
}, {
  name: 'Theta',
  value: -15,
  limit: -50,
  unit: 'k€/day'
}];
const stressScenarios = [{
  name: 'Price Spike (+50%)',
  impact: -120000,
  prob: 'Low'
}, {
  name: 'Wind Drop (-80%)',
  impact: -45000,
  prob: 'Med'
}, {
  name: 'Nuke Outage',
  impact: -250000,
  prob: 'V. Low'
}, {
  name: 'Demand Crash',
  impact: 80000,
  prob: 'Low'
}];
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-2912">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-2913">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-2914">
            <span className="text-slate-600 capitalize" data-id="element-2915">{entry.name}:</span>
            <span className={`font-mono font-medium ${entry.value < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-2916">
              €{Math.abs(entry.value).toLocaleString()}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function PositionRiskDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-slate-50" data-id="element-2917">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2918">
        <div data-id="element-2919">
          <div className="flex items-center gap-2 mb-1" data-id="element-2920">
            <div className="p-2 bg-rose-500 rounded-lg" data-id="element-2921">
              <ShieldAlert size={20} className="text-white" data-id="element-2922" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2923">
              Risk Control
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-2924">
            Real-time P&L, VaR monitoring, and stress testing
          </p>
        </div>

        <div className="flex gap-4" data-id="element-2925">
          <div className="bg-white border border-slate-200 px-4 py-2 rounded-lg shadow-sm" data-id="element-2926">
            <p className="text-xs text-slate-500 uppercase font-bold" data-id="element-2927">
              Daily P&L
            </p>
            <p className="text-xl font-bold text-emerald-600" data-id="element-2928">+€14,250</p>
          </div>
          <div className="bg-white border border-slate-200 px-4 py-2 rounded-lg shadow-sm" data-id="element-2929">
            <p className="text-xs text-slate-500 uppercase font-bold" data-id="element-2930">
              VaR (95%)
            </p>
            <p className="text-xl font-bold text-rose-600" data-id="element-2931">€5,420</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2932">
        {/* P&L vs VaR Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2933">
          <div className="flex justify-between items-center mb-6" data-id="element-2934">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2935">
              Intraday P&L Evolution
            </h3>
            <div className="flex gap-4 text-xs" data-id="element-2936">
              <span className="flex items-center gap-1" data-id="element-2937">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-2938"></div> P&L
              </span>
              <span className="flex items-center gap-1" data-id="element-2939">
                <div className="w-2 h-2 rounded-full bg-rose-200" data-id="element-2940"></div> VaR
                Limit
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-2941">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2942">
              <AreaChart data={pnlHistory} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2943">
                <defs data-id="element-2944">
                  <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1" data-id="element-2945">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-2946" />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-2947" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-2948" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-2949" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} data-id="element-2950" />
                <Tooltip content={<CustomTooltip data-id="element-2952" />} data-id="element-2951" />
                <ReferenceLine y={0} stroke="#94a3b8" data-id="element-2953" />
                <Area type="monotone" dataKey="var" name="VaR Limit" stroke="none" fill="#fecdd3" fillOpacity={0.4} data-id="element-2954" />
                <Area type="monotone" dataKey="pnl" name="P&L" stroke="#10b981" fill="url(#colorPnl)" strokeWidth={2} data-id="element-2955" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Risk Limits (Greeks) */}
        <div className="space-y-6" data-id="element-2956">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2957">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-2958">
              Risk Limits Utilization
            </h3>
            <div className="space-y-5" data-id="element-2959">
              {riskMetrics.map(metric => {
              const usage = metric.value / metric.limit * 100;
              const color = usage > 90 ? 'bg-rose-500' : usage > 70 ? 'bg-amber-500' : 'bg-emerald-500';
              return <div key={metric.name} data-id="element-2960">
                    <div className="flex justify-between text-sm mb-1" data-id="element-2961">
                      <span className="font-medium text-slate-700" data-id="element-2962">
                        {metric.name}
                      </span>
                      <span className="text-slate-500" data-id="element-2963">
                        {metric.value} / {metric.limit}{' '}
                        <span className="text-xs" data-id="element-2964">{metric.unit}</span>
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2965">
                      <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${usage}%`
                  }} className={`h-full rounded-full ${color}`} data-id="element-2966" />
                    </div>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Stress Tests */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2967">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-2968">
              Stress Scenarios
            </h3>
            <div className="space-y-3" data-id="element-2969">
              {stressScenarios.map((scenario, idx) => <div key={idx} className="flex justify-between items-center p-2 rounded hover:bg-slate-50 transition-colors" data-id="element-2970">
                  <div className="flex items-center gap-2" data-id="element-2971">
                    <AlertTriangle size={14} className="text-slate-400" data-id="element-2972" />
                    <span className="text-sm text-slate-700" data-id="element-2973">
                      {scenario.name}
                    </span>
                  </div>
                  <span className={`text-sm font-mono font-bold ${scenario.impact < 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-id="element-2974">
                    {scenario.impact > 0 ? '+' : ''}€
                    {(scenario.impact / 1000).toFixed(0)}k
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}